import type { SupportedLocale } from '@erp/shared';

const en = {
  open: 'Import Lexware ZIP',
  title: 'Upload a Lexware backup',
  description:
    'Upload your ZIP here, including backups over 10 GB. Check the preview before importing historical documents.',
  separateData:
    'The feature installation contains no backup data. Your ZIP is uploaded separately. Documents remain a standalone read-only archive without links to current master data or accounting.',
  loading: 'Loading ZIP imports',
  historyError: 'ZIP imports could not be loaded.',
  statusError: 'Import progress could not be loaded.',
  job: 'ZIP import',
  newUpload: 'New ZIP upload',
  chooseZip: 'Lexware backup file (ZIP)',
  resumeHint:
    'To resume, select the same ZIP again. Accepted parts are retained; a different file cannot continue this upload.',
  keepOpen:
    'Keep this page open during upload. You can pause and resume later, even after closing the page.',
  canClose:
    'The ZIP has been uploaded. Checking and importing continue in the background; you can close this page and return to this ZIP import later.',
  browserUnsupported:
    'This browser cannot securely verify ZIP parts. Use a current browser with a secure connection.',
  preparing: 'Checking the selected ZIP for a safe continuation…',
  start: 'Start upload',
  resume: 'Resume upload',
  pause: 'Pause upload',
  paused: 'Upload paused. Accepted parts are retained.',
  uploadProgress: 'ZIP upload progress',
  bytesProgress: '{{received}} of {{total}} uploaded',
  preview: 'Review preview',
  previewUnavailable:
    'The preview is incomplete. Importing is unavailable. Reload the progress or select another ZIP.',
  sourceTenants: 'Historical source companies',
  confirmHint:
    'Review the document counts and source companies. Importing creates only historical documents and protected originals, without customer, product or accounting assignments. Empty or unreadable text remains marked; .xort files are ignored.',
  confirm: 'Import historical documents',
  confirming: 'Starting import…',
  importProgress: 'Historical document import progress',
  documentsProgress: '{{imported}} of {{total}} documents imported',
  failedHint:
    'Retry checking or importing with the uploaded ZIP, without uploading it again. Repeating an identical import does not duplicate documents.',
  retryBackground: 'Retry check / import',
  retrying: 'Retrying…',
  refreshArchive: 'Refresh archive',
  anotherUpload: 'Select another ZIP',
  errors: {
    invalidZip: 'Select a non-empty ZIP backup up to 20 GiB with a valid file name.',
    fileMismatch: 'Select the originally chosen ZIP. This file does not match the paused upload.',
    uploadInterrupted:
      'Upload interrupted. Accepted parts are retained. Resume to retry from the confirmed progress.',
    confirmError: 'The import could not be started. Reload the progress and try again.',
  },
  statuses: {
    UPLOADING: 'ZIP upload in progress',
    QUEUED: 'ZIP waiting for checking',
    EXTRACTING: 'ZIP being checked',
    PREVIEW_READY: 'Preview ready',
    IMPORTING: 'Importing historical documents',
    COMPLETED: 'Archive ready',
    FAILED: 'Import stopped',
  },
  counts: {
    totalDocuments: 'PDF documents',
    textDocuments: 'PDFs with readable text',
    imageOnlyDocuments: 'Scans without readable text',
    textErrors: 'PDFs with text extraction errors',
    truncatedDocuments: 'PDFs with text excerpts',
    ignoredXortFiles: 'Ignored .xort files',
    excludedFiles: 'Other excluded files',
    unclassifiedDocuments: 'Documents with an unresolved type',
    documentsWithCustomerNumber: 'Documents with an old customer number',
    documentsWithDocumentNumber: 'Documents with an old document number',
  },
  failureCodes: {
    ARCHIVE_INVALID:
      'The ZIP could not be read as a Lexware backup. Check the file and select another ZIP.',
    EXTRACTION_FAILED:
      'The backup could not be checked completely. Select another ZIP or contact your administrator.',
    IMPORT_FAILED:
      'The historical documents could not be fully imported. Already imported documents remain in the archive.',
    PERMISSION_REVOKED:
      'The import was stopped because the required permissions are no longer available.',
    STORAGE_UNAVAILABLE: 'The backup storage is currently unavailable. Contact your administrator.',
    UNKNOWN: 'The import could not be completed. Contact your administrator.',
  },
};

export const lexwareArchiveImportTranslations: Record<SupportedLocale, typeof en> = {
  en,
  de: {
    open: 'Lexware ZIP importieren',
    title: 'Lexware-Sicherung hochladen',
    description:
      'Laden Sie hier Ihre ZIP hoch, auch Sicherungen über 10 GB. Prüfen Sie die Vorschau, bevor Sie Altbelege übernehmen.',
    separateData:
      'Die Installation dieser Funktion enthält keine Sicherungsdaten. Ihre ZIP wird separat hochgeladen. Belege bleiben ein eigenständiges Lese-Archiv ohne Zuordnung zu aktuellen Stammdaten oder zur Buchhaltung.',
    loading: 'ZIP-Importe werden geladen',
    historyError: 'ZIP-Importe konnten nicht geladen werden.',
    statusError: 'Der Importfortschritt konnte nicht geladen werden.',
    job: 'ZIP-Import',
    newUpload: 'Neue ZIP hochladen',
    chooseZip: 'Lexware-Sicherungsdatei (ZIP)',
    resumeHint:
      'Wählen Sie zum Fortsetzen dieselbe ZIP erneut aus. Bestätigte Teile bleiben erhalten; eine andere Datei kann diesen Upload nicht fortsetzen.',
    keepOpen:
      'Lassen Sie diese Seite während des Uploads geöffnet. Sie können pausieren und später fortsetzen, auch nach dem Schließen der Seite.',
    canClose:
      'Die ZIP ist hochgeladen. Prüfung und Übernahme laufen im Hintergrund weiter; Sie können die Seite schließen und später zu diesem ZIP-Import zurückkehren.',
    browserUnsupported:
      'Dieser Browser kann ZIP-Teile nicht sicher prüfen. Verwenden Sie einen aktuellen Browser mit einer sicheren Verbindung.',
    preparing: 'Die ausgewählte ZIP wird für eine sichere Fortsetzung geprüft…',
    start: 'Upload starten',
    resume: 'Upload fortsetzen',
    pause: 'Upload pausieren',
    paused: 'Upload pausiert. Bestätigte Teile bleiben erhalten.',
    uploadProgress: 'Fortschritt des ZIP-Uploads',
    bytesProgress: '{{received}} von {{total}} hochgeladen',
    preview: 'Vorschau prüfen',
    previewUnavailable:
      'Die Vorschau ist unvollständig. Die Übernahme ist gesperrt. Laden Sie den Fortschritt erneut oder wählen Sie eine andere ZIP.',
    sourceTenants: 'Historische Quellfirmen',
    confirmHint:
      'Prüfen Sie Belegzahlen und Quellfirmen. Die Übernahme erzeugt ausschließlich Altbelege und geschützte Originale ohne Kunden-, Artikel- oder Buchhaltungszuordnung. Leerer oder unlesbarer Text bleibt gekennzeichnet; .xort-Dateien werden ignoriert.',
    confirm: 'Altbelege übernehmen',
    confirming: 'Übernahme wird gestartet…',
    importProgress: 'Fortschritt der Altbelegübernahme',
    documentsProgress: '{{imported}} von {{total}} Belegen übernommen',
    failedHint:
      'Wiederholen Sie Prüfung oder Übernahme mit der hochgeladenen ZIP ohne erneuten Upload. Eine identische Übernahme erzeugt keine doppelten Belege.',
    retryBackground: 'Prüfung / Übernahme wiederholen',
    retrying: 'Wird wiederholt…',
    refreshArchive: 'Archiv aktualisieren',
    anotherUpload: 'Andere ZIP auswählen',
    errors: {
      invalidZip:
        'Wählen Sie eine nicht leere ZIP-Sicherung bis 20 GiB mit einem gültigen Dateinamen.',
      fileMismatch:
        'Wählen Sie die ursprünglich ausgewählte ZIP. Diese Datei passt nicht zum pausierten Upload.',
      uploadInterrupted:
        'Upload unterbrochen. Bestätigte Teile bleiben erhalten. Setzen Sie den Upload ab dem bestätigten Fortschritt fort.',
      confirmError:
        'Die Übernahme konnte nicht gestartet werden. Laden Sie den Fortschritt erneut und versuchen Sie es noch einmal.',
    },
    statuses: {
      UPLOADING: 'ZIP wird hochgeladen',
      QUEUED: 'ZIP wartet auf Prüfung',
      EXTRACTING: 'ZIP wird geprüft',
      PREVIEW_READY: 'Vorschau bereit',
      IMPORTING: 'Altbelege werden übernommen',
      COMPLETED: 'Archiv bereit',
      FAILED: 'Import angehalten',
    },
    counts: {
      totalDocuments: 'PDF-Belege',
      textDocuments: 'PDFs mit lesbarem Text',
      imageOnlyDocuments: 'Scans ohne lesbaren Text',
      textErrors: 'PDFs mit Fehlern beim Textauslesen',
      truncatedDocuments: 'PDFs mit Textauszug',
      ignoredXortFiles: 'Ignorierte .xort-Dateien',
      excludedFiles: 'Weitere ausgeschlossene Dateien',
      unclassifiedDocuments: 'Belege mit ungeklärter Art',
      documentsWithCustomerNumber: 'Belege mit alter Kundennummer',
      documentsWithDocumentNumber: 'Belege mit alter Belegnummer',
    },
    failureCodes: {
      ARCHIVE_INVALID:
        'Die ZIP konnte nicht als Lexware-Sicherung gelesen werden. Prüfen Sie die Datei und wählen Sie eine andere ZIP.',
      EXTRACTION_FAILED:
        'Die Sicherung konnte nicht vollständig geprüft werden. Wählen Sie eine andere ZIP oder wenden Sie sich an Ihre Administration.',
      IMPORT_FAILED:
        'Die Altbelege konnten nicht vollständig übernommen werden. Bereits übernommene Belege bleiben im Archiv.',
      PERMISSION_REVOKED:
        'Der Import wurde angehalten, weil die erforderlichen Berechtigungen nicht mehr vorliegen.',
      STORAGE_UNAVAILABLE:
        'Der Speicher für Sicherungen ist derzeit nicht verfügbar. Wenden Sie sich an Ihre Administration.',
      UNKNOWN:
        'Der Import konnte nicht abgeschlossen werden. Wenden Sie sich an Ihre Administration.',
    },
  },
  fr: {
    open: 'Importer un ZIP Lexware',
    title: 'Téléverser une sauvegarde Lexware',
    description:
      'Téléversez votre ZIP ici, y compris les sauvegardes de plus de 10 Go. Vérifiez l’aperçu avant d’importer les documents historiques.',
    separateData:
      'L’installation de cette fonction ne contient aucune sauvegarde. Votre ZIP est téléversé séparément. Les documents restent une archive autonome en lecture seule, sans lien avec les fiches actuelles ou la comptabilité.',
    loading: 'Chargement des imports ZIP',
    historyError: 'Impossible de charger les imports ZIP.',
    statusError: 'Impossible de charger la progression de l’import.',
    job: 'Import ZIP',
    newUpload: 'Téléverser un nouveau ZIP',
    chooseZip: 'Sauvegarde Lexware (ZIP)',
    resumeHint:
      'Pour reprendre, sélectionnez à nouveau le même ZIP. Les parties confirmées sont conservées ; un autre fichier ne peut pas poursuivre ce téléversement.',
    keepOpen:
      'Gardez cette page ouverte pendant le téléversement. Vous pouvez le suspendre et le reprendre plus tard, même après avoir fermé la page.',
    canClose:
      'Le ZIP est téléversé. La vérification et l’import continuent en arrière-plan ; vous pouvez fermer cette page et revenir à cet import ZIP plus tard.',
    browserUnsupported:
      'Ce navigateur ne peut pas vérifier les parties du ZIP de manière sûre. Utilisez un navigateur récent avec une connexion sécurisée.',
    preparing: 'Vérification du ZIP sélectionné pour une reprise sûre…',
    start: 'Démarrer le téléversement',
    resume: 'Reprendre le téléversement',
    pause: 'Suspendre le téléversement',
    paused: 'Téléversement suspendu. Les parties confirmées sont conservées.',
    uploadProgress: 'Progression du téléversement ZIP',
    bytesProgress: '{{received}} sur {{total}} téléversés',
    preview: 'Vérifier l’aperçu',
    previewUnavailable:
      'L’aperçu est incomplet. L’import est indisponible. Rechargez la progression ou sélectionnez un autre ZIP.',
    sourceTenants: 'Sociétés sources historiques',
    confirmHint:
      'Vérifiez les nombres de documents et les sociétés sources. L’import crée uniquement des documents historiques et leurs originaux protégés, sans affectation aux clients, articles ou à la comptabilité. Les textes vides ou illisibles restent signalés ; les fichiers .xort sont ignorés.',
    confirm: 'Importer les documents historiques',
    confirming: 'Démarrage de l’import…',
    importProgress: 'Progression de l’import des documents',
    documentsProgress: '{{imported}} documents importés sur {{total}}',
    failedHint:
      'Relancez la vérification ou l’import avec le ZIP déjà téléversé, sans le téléverser à nouveau. Répéter un import identique ne crée pas de doublons.',
    retryBackground: 'Relancer la vérification / l’import',
    retrying: 'Nouvel essai…',
    refreshArchive: 'Actualiser l’archive',
    anotherUpload: 'Choisir un autre ZIP',
    errors: {
      invalidZip:
        'Sélectionnez une sauvegarde ZIP non vide de 20 Gio maximum avec un nom de fichier valide.',
      fileMismatch:
        'Sélectionnez le ZIP choisi initialement. Ce fichier ne correspond pas au téléversement suspendu.',
      uploadInterrupted:
        'Téléversement interrompu. Les parties confirmées sont conservées. Reprenez à partir de la progression confirmée.',
      confirmError: 'Impossible de démarrer l’import. Rechargez la progression et réessayez.',
    },
    statuses: {
      UPLOADING: 'Téléversement du ZIP en cours',
      QUEUED: 'ZIP en attente de vérification',
      EXTRACTING: 'Vérification du ZIP en cours',
      PREVIEW_READY: 'Aperçu prêt',
      IMPORTING: 'Import des documents historiques en cours',
      COMPLETED: 'Archive prête',
      FAILED: 'Import arrêté',
    },
    counts: {
      totalDocuments: 'Documents PDF',
      textDocuments: 'PDF avec texte lisible',
      imageOnlyDocuments: 'Scans sans texte lisible',
      textErrors: 'PDF avec erreur d’extraction du texte',
      truncatedDocuments: 'PDF avec extrait de texte',
      ignoredXortFiles: 'Fichiers .xort ignorés',
      excludedFiles: 'Autres fichiers exclus',
      unclassifiedDocuments: 'Documents au type non résolu',
      documentsWithCustomerNumber: 'Documents avec un ancien numéro client',
      documentsWithDocumentNumber: 'Documents avec un ancien numéro de document',
    },
    failureCodes: {
      ARCHIVE_INVALID:
        'Le ZIP n’a pas pu être lu comme une sauvegarde Lexware. Vérifiez le fichier et sélectionnez un autre ZIP.',
      EXTRACTION_FAILED:
        'La sauvegarde n’a pas pu être entièrement vérifiée. Sélectionnez un autre ZIP ou contactez votre administration.',
      IMPORT_FAILED:
        'Les documents historiques n’ont pas pu être entièrement importés. Les documents déjà importés restent dans l’archive.',
      PERMISSION_REVOKED:
        'L’import a été arrêté car les autorisations requises ne sont plus disponibles.',
      STORAGE_UNAVAILABLE:
        'Le stockage des sauvegardes est actuellement indisponible. Contactez votre administration.',
      UNKNOWN: 'L’import n’a pas pu être terminé. Contactez votre administration.',
    },
  },
  es: {
    open: 'Importar ZIP de Lexware',
    title: 'Subir una copia de Lexware',
    description:
      'Suba aquí su ZIP, también copias de más de 10 GB. Revise la vista previa antes de importar documentos históricos.',
    separateData:
      'La instalación de esta función no contiene datos de copias. Su ZIP se sube por separado. Los documentos permanecen en un archivo independiente de solo lectura, sin vínculos con fichas actuales ni contabilidad.',
    loading: 'Cargando importaciones ZIP',
    historyError: 'No se pudieron cargar las importaciones ZIP.',
    statusError: 'No se pudo cargar el progreso de la importación.',
    job: 'Importación ZIP',
    newUpload: 'Subir un ZIP nuevo',
    chooseZip: 'Copia de seguridad de Lexware (ZIP)',
    resumeHint:
      'Para continuar, seleccione de nuevo el mismo ZIP. Las partes confirmadas se conservan; otro archivo no puede continuar esta subida.',
    keepOpen:
      'Mantenga esta página abierta durante la subida. Puede pausarla y continuar más tarde, incluso después de cerrar la página.',
    canClose:
      'El ZIP ya se ha subido. La comprobación e importación continúan en segundo plano; puede cerrar la página y volver a esta importación ZIP más tarde.',
    browserUnsupported:
      'Este navegador no puede verificar las partes del ZIP de forma segura. Use un navegador actualizado con una conexión segura.',
    preparing: 'Comprobando el ZIP seleccionado para continuar de forma segura…',
    start: 'Iniciar subida',
    resume: 'Continuar subida',
    pause: 'Pausar subida',
    paused: 'Subida pausada. Se conservan las partes confirmadas.',
    uploadProgress: 'Progreso de subida del ZIP',
    bytesProgress: '{{received}} de {{total}} subidos',
    preview: 'Revisar vista previa',
    previewUnavailable:
      'La vista previa está incompleta. No se puede importar. Recargue el progreso o seleccione otro ZIP.',
    sourceTenants: 'Empresas de origen históricas',
    confirmHint:
      'Revise las cantidades de documentos y empresas de origen. La importación crea solo documentos históricos y originales protegidos, sin asignaciones a clientes, artículos o contabilidad. Los textos vacíos o ilegibles permanecen indicados; se ignoran los archivos .xort.',
    confirm: 'Importar documentos históricos',
    confirming: 'Iniciando importación…',
    importProgress: 'Progreso de importación de documentos',
    documentsProgress: '{{imported}} de {{total}} documentos importados',
    failedHint:
      'Repita la comprobación o importación con el ZIP ya subido, sin volver a subirlo. Repetir una importación idéntica no duplica documentos.',
    retryBackground: 'Repetir comprobación / importación',
    retrying: 'Reintentando…',
    refreshArchive: 'Actualizar archivo',
    anotherUpload: 'Seleccionar otro ZIP',
    errors: {
      invalidZip:
        'Seleccione una copia ZIP no vacía de hasta 20 GiB con un nombre de archivo válido.',
      fileMismatch:
        'Seleccione el ZIP elegido originalmente. Este archivo no coincide con la subida pausada.',
      uploadInterrupted:
        'Subida interrumpida. Se conservan las partes confirmadas. Continúe desde el progreso confirmado.',
      confirmError: 'No se pudo iniciar la importación. Recargue el progreso e inténtelo de nuevo.',
    },
    statuses: {
      UPLOADING: 'Subiendo ZIP',
      QUEUED: 'ZIP pendiente de comprobación',
      EXTRACTING: 'Comprobando ZIP',
      PREVIEW_READY: 'Vista previa lista',
      IMPORTING: 'Importando documentos históricos',
      COMPLETED: 'Archivo listo',
      FAILED: 'Importación detenida',
    },
    counts: {
      totalDocuments: 'Documentos PDF',
      textDocuments: 'PDF con texto legible',
      imageOnlyDocuments: 'Escaneos sin texto legible',
      textErrors: 'PDF con errores de extracción de texto',
      truncatedDocuments: 'PDF con extractos de texto',
      ignoredXortFiles: 'Archivos .xort ignorados',
      excludedFiles: 'Otros archivos excluidos',
      unclassifiedDocuments: 'Documentos con tipo sin resolver',
      documentsWithCustomerNumber: 'Documentos con antiguo número de cliente',
      documentsWithDocumentNumber: 'Documentos con antiguo número de documento',
    },
    failureCodes: {
      ARCHIVE_INVALID:
        'El ZIP no se pudo leer como copia de Lexware. Compruebe el archivo y seleccione otro ZIP.',
      EXTRACTION_FAILED:
        'No se pudo comprobar toda la copia. Seleccione otro ZIP o contacte con su administración.',
      IMPORT_FAILED:
        'No se pudieron importar todos los documentos históricos. Los ya importados permanecen en el archivo.',
      PERMISSION_REVOKED:
        'La importación se ha detenido porque ya no se dispone de los permisos necesarios.',
      STORAGE_UNAVAILABLE:
        'El almacenamiento de copias no está disponible. Contacte con su administración.',
      UNKNOWN: 'No se pudo completar la importación. Contacte con su administración.',
    },
  },
  tr: {
    open: 'Lexware ZIP içe aktar',
    title: 'Lexware yedeği yükleyin',
    description:
      '10 GB üzerindeki yedekler dahil ZIP dosyanızı buradan yükleyin. Geçmiş belgeleri içe aktarmadan önce önizlemeyi kontrol edin.',
    separateData:
      'Bu özelliğin kurulumu yedek verisi içermez. ZIP dosyanız ayrıca yüklenir. Belgeler güncel ana verilere veya muhasebeye bağlı olmayan, bağımsız ve salt okunur bir arşiv olarak kalır.',
    loading: 'ZIP içe aktarımları yükleniyor',
    historyError: 'ZIP içe aktarımları yüklenemedi.',
    statusError: 'İçe aktarım ilerlemesi yüklenemedi.',
    job: 'ZIP içe aktarımı',
    newUpload: 'Yeni ZIP yükle',
    chooseZip: 'Lexware yedek dosyası (ZIP)',
    resumeHint:
      'Sürdürmek için aynı ZIP dosyasını yeniden seçin. Onaylanan parçalar korunur; başka bir dosya bu yüklemeyi sürdüremez.',
    keepOpen:
      'Yükleme sırasında bu sayfayı açık tutun. Yüklemeyi duraklatıp, sayfayı kapattıktan sonra da sürdürebilirsiniz.',
    canClose:
      'ZIP yüklendi. Kontrol ve içe aktarım arka planda sürer; sayfayı kapatabilir ve bu ZIP içe aktarımına daha sonra dönebilirsiniz.',
    browserUnsupported:
      'Bu tarayıcı ZIP parçalarını güvenli şekilde doğrulayamıyor. Güvenli bağlantı ile güncel bir tarayıcı kullanın.',
    preparing: 'Güvenli sürdürme için seçilen ZIP kontrol ediliyor…',
    start: 'Yüklemeyi başlat',
    resume: 'Yüklemeyi sürdür',
    pause: 'Yüklemeyi duraklat',
    paused: 'Yükleme duraklatıldı. Onaylanan parçalar korunur.',
    uploadProgress: 'ZIP yükleme ilerlemesi',
    bytesProgress: '{{total}} toplamın {{received}} kadarı yüklendi',
    preview: 'Önizlemeyi kontrol et',
    previewUnavailable:
      'Önizleme eksik. İçe aktarım kullanılamıyor. İlerlemeyi yeniden yükleyin veya başka bir ZIP seçin.',
    sourceTenants: 'Geçmiş kaynak şirketler',
    confirmHint:
      'Belge sayılarını ve kaynak şirketleri kontrol edin. İçe aktarım yalnızca geçmiş belgeler ve korunan özgün dosyalar oluşturur; müşteri, ürün veya muhasebe bağlantısı kurmaz. Boş veya okunamayan metin işaretli kalır; .xort dosyaları yok sayılır.',
    confirm: 'Geçmiş belgeleri içe aktar',
    confirming: 'İçe aktarım başlatılıyor…',
    importProgress: 'Geçmiş belge içe aktarım ilerlemesi',
    documentsProgress: '{{total}} belgeden {{imported}} tanesi içe aktarıldı',
    failedHint:
      'Yüklenen ZIP ile kontrolü veya içe aktarımı, tekrar yüklemeden yeniden deneyin. Aynı içe aktarımı tekrarlamak belgeleri çoğaltmaz.',
    retryBackground: 'Kontrolü / içe aktarımı yeniden dene',
    retrying: 'Yeniden deneniyor…',
    refreshArchive: 'Arşivi yenile',
    anotherUpload: 'Başka ZIP seç',
    errors: {
      invalidZip:
        'Geçerli dosya adına sahip, boş olmayan ve en fazla 20 GiB boyutunda bir ZIP yedeği seçin.',
      fileMismatch:
        'Başlangıçta seçilen ZIP dosyasını seçin. Bu dosya duraklatılan yüklemeyle eşleşmiyor.',
      uploadInterrupted:
        'Yükleme kesildi. Onaylanan parçalar korunur. Onaylanan ilerlemeden sürdürün.',
      confirmError: 'İçe aktarım başlatılamadı. İlerlemeyi yeniden yükleyip tekrar deneyin.',
    },
    statuses: {
      UPLOADING: 'ZIP yükleniyor',
      QUEUED: 'ZIP kontrol bekliyor',
      EXTRACTING: 'ZIP kontrol ediliyor',
      PREVIEW_READY: 'Önizleme hazır',
      IMPORTING: 'Geçmiş belgeler içe aktarılıyor',
      COMPLETED: 'Arşiv hazır',
      FAILED: 'İçe aktarım durduruldu',
    },
    counts: {
      totalDocuments: 'PDF belgeleri',
      textDocuments: 'Okunabilir metin içeren PDF’ler',
      imageOnlyDocuments: 'Okunabilir metni olmayan taramalar',
      textErrors: 'Metin çıkarma hatası olan PDF’ler',
      truncatedDocuments: 'Metin özeti olan PDF’ler',
      ignoredXortFiles: 'Yok sayılan .xort dosyaları',
      excludedFiles: 'Diğer hariç tutulan dosyalar',
      unclassifiedDocuments: 'Türü çözümlenmemiş belgeler',
      documentsWithCustomerNumber: 'Eski müşteri numarası olan belgeler',
      documentsWithDocumentNumber: 'Eski belge numarası olan belgeler',
    },
    failureCodes: {
      ARCHIVE_INVALID:
        'ZIP, Lexware yedeği olarak okunamadı. Dosyayı kontrol edip başka bir ZIP seçin.',
      EXTRACTION_FAILED:
        'Yedeğin tamamı kontrol edilemedi. Başka bir ZIP seçin veya yöneticinize başvurun.',
      IMPORT_FAILED:
        'Geçmiş belgelerin tamamı içe aktarılamadı. Önceden aktarılan belgeler arşivde kalır.',
      PERMISSION_REVOKED: 'Gerekli izinler artık bulunmadığından içe aktarım durduruldu.',
      STORAGE_UNAVAILABLE: 'Yedek depolama alanı şu anda kullanılamıyor. Yöneticinize başvurun.',
      UNKNOWN: 'İçe aktarım tamamlanamadı. Yöneticinize başvurun.',
    },
  },
};
